
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

// Define the shape of our sidebar feature toggles
export interface SidebarFeature {
  id: string;
  name: string;
  enabled: boolean;
}

interface AdminContextType {
  isAdmin: boolean;
  impersonatingBusinessUnit: number | null;
  setImpersonatingBusinessUnit: (id: number | null) => void;
  sidebarFeatures: SidebarFeature[];
  updateSidebarFeature: (id: string, enabled: boolean) => void;
  isFeatureEnabled: (id: string) => boolean;
}

const initialSidebarFeatures: SidebarFeature[] = [
  { id: 'dashboard', name: 'Dashboard', enabled: true },
  { id: 'companies', name: 'Companies', enabled: true },
  { id: 'recipes', name: 'Recipes', enabled: true },
  { id: 'menu-engineering', name: 'Menu Performance', enabled: true },
  { id: 'inventory-intelligence', name: 'Inventory Intelligence', enabled: true },
  { id: 'supplier-relationship', name: 'Supplier Relationship', enabled: true },
  { id: 'dynamic-pricing', name: 'Dynamic Pricing', enabled: true },
  { id: 'waste-reduction', name: 'Waste Reduction', enabled: false },
  { id: 'procurement-calendar', name: 'Procurement Calendar', enabled: true },
  { id: 'recipe-innovation', name: 'Recipe Innovation', enabled: false },
  { id: 'operational-efficiency', name: 'Operational Efficiency', enabled: false },
  { id: 'financial-performance', name: 'Financial Performance', enabled: true },
  { id: 'ai-assistant', name: 'AI Assistant', enabled: true },
  { id: 'reports', name: 'Reports', enabled: true },
  { id: 'settings', name: 'Settings', enabled: true },
];

// Load stored settings from localStorage
const loadStoredSettings = () => {
  try {
    // Load sidebar features
    const storedFeatures = localStorage.getItem('sidebarFeatures');
    const features = storedFeatures ? JSON.parse(storedFeatures) : initialSidebarFeatures;
    
    // Load impersonating business unit
    const storedImpersonatingUnit = localStorage.getItem('impersonatingBusinessUnit');
    const impersonatingUnit = storedImpersonatingUnit ? JSON.parse(storedImpersonatingUnit) : null;
    
    return { features, impersonatingUnit };
  } catch (error) {
    console.error('Error loading stored settings:', error);
    return { features: initialSidebarFeatures, impersonatingUnit: null };
  }
};

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  // Set isAdmin to true for all users
  const [isAdmin, setIsAdmin] = useState(true);
  
  // Load stored settings
  const storedSettings = loadStoredSettings();
  const [impersonatingBusinessUnit, setImpersonatingBusinessUnit] = useState<number | null>(storedSettings.impersonatingUnit);
  const [sidebarFeatures, setSidebarFeatures] = useState<SidebarFeature[]>(storedSettings.features);

  useEffect(() => {
    // For now, all users are admins
    setIsAdmin(true);
  }, [user]);

  // Persist impersonating business unit to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('impersonatingBusinessUnit', JSON.stringify(impersonatingBusinessUnit));
    } catch (error) {
      console.error('Error saving impersonating business unit:', error);
    }
  }, [impersonatingBusinessUnit]);

  // Persist sidebar features to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('sidebarFeatures', JSON.stringify(sidebarFeatures));
    } catch (error) {
      console.error('Error saving sidebar features:', error);
    }
  }, [sidebarFeatures]);

  const updateSidebarFeature = (id: string, enabled: boolean) => {
    setSidebarFeatures(features => 
      features.map(feature => 
        feature.id === id ? { ...feature, enabled } : feature
      )
    );
  };

  const isFeatureEnabled = (id: string) => {
    const feature = sidebarFeatures.find(f => f.id === id);
    return feature?.enabled || false;
  };

  return (
    <AdminContext.Provider value={{ 
      isAdmin, 
      impersonatingBusinessUnit, 
      setImpersonatingBusinessUnit,
      sidebarFeatures,
      updateSidebarFeature,
      isFeatureEnabled
    }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = (): AdminContextType => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
